import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, FormControlLabel, IconButton, InputAdornment, Switch, TextField, Typography } from "@material-ui/core";
import { AttachFile, Close, ExpandMore, SendOutlined, Visibility, VisibilityOff } from "@material-ui/icons";
import { buildPayload, initialValues, validateMessage } from "./helpers";
import useStyles from "./styles";

export default function SendForm({ mode, endpoint, onBusyChange = () => {} }) {
  const classes = useStyles();
  const media = mode === "media";
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState(null);
  const lock = useRef(false);
  const fileInput = useRef(null);
  const responseRef = useRef(null);
  useEffect(() => {
    if (response && responseRef.current && responseRef.current.scrollIntoView) {
      responseRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [response]);
  const change = event => {
    const { name, value, checked, type } = event.target;
    setValues(current => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setErrors(current => ({ ...current, [name]: undefined }));
  };
  const submit = async event => {
    event.preventDefault();
    if (lock.current) return;
    const nextErrors = validateMessage(values, mode, file);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      if (nextErrors.userId || nextErrors.queueId) setExpanded(true);
      return;
    }
    lock.current = true;
    setBusy(true);
    onBusyChange(true);
    setResponse(null);
    try {
      const result = await axios.request({
        url: endpoint, method: "POST", data: buildPayload(values, media ? file : null),
        headers: { Authorization: `Bearer ${values.token.trim()}`, ...(!media ? { "Content-Type": "application/json" } : {}) }
      });
      setResponse({ success: true, status: result.status, data: result.data });
    } catch (error) {
      setResponse({ success: false, status: error.response && error.response.status, data: error.response ? error.response.data : null });
    } finally {
      lock.current = false;
      setBusy(false);
      onBusyChange(false);
    }
  };
  const field = (name, label, props = {}) => <TextField id={`api-${mode}-${name}`} name={name} label={label} value={values[name]} onChange={change} variant="outlined" size="small" fullWidth disabled={busy} {...props} error={Boolean(errors[name])} helperText={errors[name] || props.helperText} />;
  return <section className={classes.card} aria-label={media ? "Teste de arquivo" : "Teste de texto"}>
    <div className={classes.cardHeader}><Typography component="h2">Testar envio</Typography><SendOutlined color="primary" fontSize="small" /></div>
    <div className={classes.cardBody}>
      <form onSubmit={submit} noValidate autoComplete="off" className={classes.form}>
        {field("token", "Token da conexão", { required: true, type: visible ? "text" : "password", autoComplete: "new-password", helperText: errors.token || "Conexões → Editar conexão → Token", InputProps: { endAdornment: <InputAdornment position="end"><IconButton size="small" disabled={busy} aria-label={visible ? "Ocultar token" : "Mostrar token"} onClick={() => setVisible(value => !value)}>{visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> } })}
        {field("number", "Número do WhatsApp", { required: true, placeholder: "5551999999999", inputProps: { inputMode: "numeric" }, helperText: errors.number || "DDI + DDD + número, sem espaços ou símbolos." })}
        {media && <div>
          <input ref={fileInput} hidden type="file" aria-label="Arquivo para enviar" disabled={busy} onChange={event => { setFile(event.target.files[0] || null); setErrors(current => ({ ...current, file: undefined })); }} />
          <div className={classes.fileArea}>
            <AttachFile color="primary" />
            <div>{file ? <Typography variant="body2">{file.name}</Typography> : <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInput.current.click()}>Selecionar arquivo</Button>}<Typography className={classes.muted}>{file ? `${Math.max(1, Math.round(file.size / 1024))} KB` : "Uma imagem, documento, áudio ou vídeo"}</Typography></div>
            {file && <IconButton size="small" disabled={busy} aria-label="Remover arquivo" onClick={() => { setFile(null); fileInput.current.value = ""; }}><Close fontSize="small" /></IconButton>}
          </div>
          {errors.file && <Typography variant="caption" className={classes.error} role="alert">{errors.file}</Typography>}
        </div>}
        {field("body", media ? "Legenda (opcional)" : "Mensagem", { required: !media, multiline: true, minRows: 3, maxRows: 10, placeholder: media ? "Se ficar vazia, usaremos o nome do arquivo." : "Escreva a mensagem que será enviada." })}
        <Accordion expanded={expanded} onChange={(_, value) => setExpanded(value)} className={classes.accordion}>
          <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMore />}><Typography className={classes.stepTitle}>Opções do atendimento</Typography></AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <div className={classes.optionalGrid}>
              {field("userId", "ID do atendente", { inputProps: { inputMode: "numeric" } })}
              {field("queueId", "ID da fila", { inputProps: { inputMode: "numeric" } })}
            </div>
            <Typography className={classes.muted}>A fila é aplicada junto com o atendente. Sem uma fila válida, usa a primeira da conexão.</Typography>
            <div className={classes.switches}>
              <FormControlLabel control={<Switch color="primary" name="sendSignature" checked={values.sendSignature} onChange={change} disabled={busy} />} label="Incluir assinatura do atendente" />
              <FormControlLabel control={<Switch color="primary" name="closeTicket" checked={values.closeTicket} onChange={change} disabled={busy} />} label="Encerrar atendimento após o envio" />
            </div>
            {values.closeTicket && <Typography className={classes.muted}>O encerramento tem prioridade sobre a atribuição ao atendente.</Typography>}
          </AccordionDetails>
        </Accordion>
        <div className={classes.formFooter}>
          <Typography className={classes.muted}>Envia uma mensagem real para o número informado.</Typography>
          <Button type="submit" color="primary" variant="contained" disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SendOutlined />}>{busy ? "Enviando…" : media ? "Enviar arquivo" : "Enviar mensagem"}</Button>
        </div>
      </form>
      {response && <div ref={responseRef} role="status" aria-live="polite" className={classes.response}>
        <Typography className={`${classes.stepTitle} ${response.success ? classes.success : classes.error}`}>{response.status ? `HTTP ${response.status} · ${response.success ? "Solicitação processada" : "Não foi possível enviar"}` : "Não foi possível confirmar o envio"}</Typography>
        <Typography className={classes.muted}>{response.success ? "A API processou a solicitação. A entrega e a leitura devem ser conferidas no atendimento." : response.status === 401 ? "Confira o token em Conexões → Editar. Seus dados foram mantidos para corrigir e tentar novamente." : response.status ? "Confira o erro abaixo. Seus dados foram mantidos." : "Confira o atendimento antes de tentar novamente: o envio pode ter sido processado."}</Typography>
        {response.data != null && <pre className={classes.code}>{typeof response.data === "string" ? response.data : JSON.stringify(response.data, null, 2)}</pre>}
      </div>}
    </div>
  </section>;
}
